import GridLayout from "components/GridLayout";
import Container from "components/Container";
import Section from "components/Section";
import Layout from "components/Layout";
import Navbar from "components/Navbar";
import Image from "next/image";
import Link from "next/link";

import { TypeEmployeeFields, TypeTeamPageFields } from "contentful-generated";
import linkedinBlueIcon from "public/images/icon-linkedin-blue.svg";
import { getContentfulImageUrl } from "utils/utils";
import { useTranslation } from "react-i18next";
import { getTeamPage } from "repos/teamPage";
import { GetStaticPropsContext } from "next";

export async function getStaticProps({ locale = "en" }: GetStaticPropsContext) {
  const homePage = await getTeamPage({ locale });
  return { props: homePage };
}

export default function TeamPage({ employees }: TypeTeamPageFields) {
  const { t } = useTranslation();

  return (
    <Layout title={t("our_company") || ""}>
      <div className="bg-black">
        <Container tag="header" className="pb-20">
          <Navbar />
        </Container>
      </div>
      <main>
        <div className="bg-pink-salt">
          <Section className="pt-8 md:pt-16 pb-24 md:pb-48">
            <GridLayout className="items-center mb-16 md:mb-32">
              <h1 className="text-r-7xl font-bold max-w-[12ch] leading-none">
                {t("team_page_title")}
              </h1>
              <p className="text-neutral-600 text-r-lg leading-1.2 font-semibold max-w-[55ch]">
                {t("team_page_description")}
              </p>
            </GridLayout>
            <h2 className="text-neutral-500 text-r-xl font-bold leading-1.1 mb-10">
              {t("leadership")}
            </h2>
            <div className="flex flex-wrap gap-8 pt-4">
              {employees.map((employee) => (
                <LeadershipArticle
                  key={employee.sys.id}
                  employee={employee.fields}
                />
              ))}
            </div>
          </Section>
        </div>
      </main>
    </Layout>
  );
}

function LeadershipArticle({ employee }: { employee: TypeEmployeeFields }) {
  const { image, firstName, lastName, jobTitle, linkedInUrl, presentation } =
    employee;

  return (
    <article
      className="rounded-lg bg-white flex flex-col"
      style={{ minHeight: "400px", width: "282px" }}
    >
      <Image
        src={getContentfulImageUrl(image.fields.file.url)}
        alt={`${firstName} ${lastName}`}
        className="rounded-t-lg object-cover object-top"
        style={{ height: "220px", width: "282px" }}
        width={282}
        height={220}
      />
      <div className="text-revamp-neutral-700 flex flex-col gap-2 py-6 px-4 h-full">
        <div className="flex items-center">
          <p className="font-bold text-r-lg leading-mid">
            {firstName} {lastName}
          </p>
          {linkedInUrl && (
            <Link href={linkedInUrl}>
              <Image
                className="ml-4"
                alt="logo linkedin"
                src={linkedinBlueIcon}
                width={16}
                height={16}
              />
            </Link>
          )}
        </div>
        {jobTitle && (
          <p className="text-r-base font-bold leading-mid">{jobTitle}</p>
        )}
        {presentation && (
          <p className="text-r-lg leading-mid">{presentation}</p>
        )}
      </div>
    </article>
  );
}
